import React, { useRef, useState, useLayoutEffect } from "react";

const isImageIcon = (icon) =>
  typeof icon === "string" && (icon.startsWith("data:") || icon.startsWith("http") || /\.(png|jpe?g|svg|gif|webp)$/i.test(icon));

const isTransparentColor = (value) =>
  value == null || value === "" || value === "none" || value === "transparent";

// Chip height is ~24–28px; radius ≥ half height yields a classic pill
// (round left/right caps, straight top/bottom). Map 0→1 to 0→14px so
// intermediate values stay visibly distinct (%, by contrast, draws ovals).
const CHIP_RADIUS_MAX_PX = 14;
const ICON_BASE_PX = 16;

const resolveBorderRadius = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) {
    return `${CHIP_RADIUS_MAX_PX}px`;
  }
  return `${Math.min(1, Math.max(0, n)) * CHIP_RADIUS_MAX_PX}px`;
};

const resolveIconScale = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) {
    return 1;
  }
  return Math.min(2, Math.max(1, n));
};

// A label renders as a combinable chip: optional icon (emoji or image) followed
// by the name. Background/text colors apply only when explicitly set —
// otherwise the chip is transparent (no fill).
const LabelChip = ({ label }) => {
  const hasBg = !isTransparentColor(label.bg_color);
  const hasColor = !isTransparentColor(label.color);
  const radius = resolveBorderRadius(label.border_radius == null ? 1 : label.border_radius);
  const scale = resolveIconScale(label.icon_scale == null ? 1 : label.icon_scale);
  const iconSize = ICON_BASE_PX * scale;
  const style = {
    ...(hasColor ? { color: label.color } : {}),
    backgroundColor: hasBg ? label.bg_color : "transparent",
    borderRadius: hasBg ? radius : 0,
    ...(hasBg
      ? {}
      : {
          padding: "2px 0",
        }),
  };
  const textStyle = {
    ...(label.text_bold ? { fontWeight: 700 } : {}),
    ...(label.text_italic ? { fontStyle: "italic" } : {}),
    ...(label.text_underline ? { textDecoration: "underline" } : {}),
  };

  return (
    <span className='product-label' style={style} title={label.name}>
      {label.icon ? (
        isImageIcon(label.icon) ? (
          <img
            className='product-label-img'
            src={label.icon}
            alt=''
            style={{ height: iconSize, width: "auto" }}
          />
        ) : (
          <span className='product-label-emoji' style={{ fontSize: `${Math.round(13 * scale)}px` }}>
            {label.icon}
          </span>
        )
      ) : null}
      {!label.hide_name && label.name ? (
        <span className='product-label-text' style={textStyle}>{label.name}</span>
      ) : null}
    </span>
  );
};

// Displays product labels.
// - wrap=false (default): single line, truncated (drops overflowing labels, no ellipsis) — for product lists.
// - wrap=true: all labels shown, wrapping to new lines — for product detail views.
export default ({ labels, wrap }) => {
  const sorted = (labels || []).slice().sort((a, b) => (a.position || 0) - (b.position || 0));
  const containerRef = useRef(null);
  const ghostRef = useRef(null);
  const [visibleCount, setVisibleCount] = useState(sorted.length);

  useLayoutEffect(() => {
    if (wrap) {
      setVisibleCount(sorted.length);
      return;
    }
    const measure = () => {
      const container = containerRef.current;
      const ghost = ghostRef.current;
      if (!container || !ghost) return;
      const max = container.clientWidth;
      const gap = 4;
      let used = 0;
      let count = 0;
      const children = Array.from(ghost.children);
      for (let i = 0; i < children.length; i++) {
        const w = children[i].offsetWidth;
        const next = used === 0 ? w : used + gap + w;
        if (next <= max) {
          used = next;
          count++;
        } else {
          break;
        }
      }
      setVisibleCount(count);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(sorted.map((l) => [l.id, l.icon_scale, l.text_bold, l.text_italic, l.text_underline, l.hide_name, l.name, l.icon])), wrap]);

  if (!sorted.length) return null;

  if (wrap) {
    return (
      <div className='product-labels product-labels-wrap'>
        {sorted.map((l) => (
          <LabelChip key={l.id} label={l} />
        ))}
      </div>
    );
  }

  return (
    <div className='product-labels' ref={containerRef}>
      <div className='product-labels-ghost' ref={ghostRef} aria-hidden='true'>
        {sorted.map((l) => (
          <LabelChip key={l.id} label={l} />
        ))}
      </div>
      {sorted.slice(0, visibleCount).map((l) => (
        <LabelChip key={l.id} label={l} />
      ))}
    </div>
  );
};
